import React, { useState } from "react";
import { formatOp } from "../utils/changeLogUtils";

const EMPTY = "(empty)";

// Render one op's before/after detail.
const OpDetail = ({ op }) => {
  const d = formatOp(op);

  if (d.kind === "question-add" || d.kind === "question-del") {
    const isAdd = d.kind === "question-add";
    return (
      <div style={{ marginBottom: 4 }}>
        <span style={{ fontWeight: 600, color: isAdd ? "#059669" : "#dc2626" }}>
          {isAdd ? "Added" : "Deleted"}
        </span>{" "}
        <span style={{ color: "#334155" }}>{d.before}</span>
      </div>
    );
  }

  if (d.kind === "object") {
    return (
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontWeight: 600, color: "#475569" }}>{d.label}</div>
        {d.fields.length === 0 ? (
          <div style={{ color: "#94a3b8" }}>no visible change</div>
        ) : (
          d.fields.map((f, i) => (
            <div key={i} style={{ paddingLeft: 8 }}>
              <span style={{ color: "#64748b" }}>{f.key}: </span>
              <span
                style={{ color: "#dc2626", textDecoration: "line-through" }}
              >
                {f.before === "" ? EMPTY : f.before}
              </span>{" "}
              <span style={{ color: "#94a3b8" }}>→</span>{" "}
              <span style={{ color: "#059669" }}>
                {f.after === "" ? EMPTY : f.after}
              </span>
            </div>
          ))
        )}
      </div>
    );
  }

  if (d.kind === "array") {
    const nothing =
      d.added.length === 0 && d.removed.length === 0 && d.changed.length === 0;
    return (
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontWeight: 600, color: "#475569" }}>{d.label}</div>
        {nothing && <div style={{ color: "#94a3b8" }}>reordered / no text change</div>}
        {d.added.map((t, i) => (
          <div key={`a${i}`} style={{ color: "#059669" }}>
            + {t || EMPTY}
          </div>
        ))}
        {d.removed.map((t, i) => (
          <div key={`r${i}`} style={{ color: "#dc2626" }}>
            − {t || EMPTY}
          </div>
        ))}
        {d.changed.map((c, i) => (
          <div key={`c${i}`}>
            <span style={{ color: "#dc2626", textDecoration: "line-through" }}>
              {c.before || EMPTY}
            </span>{" "}
            <span style={{ color: "#94a3b8" }}>→</span>{" "}
            <span style={{ color: "#059669" }}>{c.after || EMPTY}</span>
          </div>
        ))}
      </div>
    );
  }

  // scalar
  return (
    <div style={{ marginBottom: 6 }}>
      <span style={{ fontWeight: 600, color: "#475569" }}>{d.label}: </span>
      <span style={{ color: "#dc2626", textDecoration: "line-through" }}>
        {d.before === "" || d.before == null ? EMPTY : String(d.before)}
      </span>{" "}
      <span style={{ color: "#94a3b8" }}>→</span>{" "}
      <span style={{ color: "#059669" }}>
        {d.after === "" || d.after == null ? EMPTY : String(d.after)}
      </span>
    </div>
  );
};

const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const initialOf = (name) => (name || "?").charAt(0).toUpperCase();

// Deterministic avatar colour from the username.
const colorOf = (name) => {
  const palette = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

const ChangeLogModal = ({
  isOpen,
  onClose,
  entries = [],
  onRevert,
  filterQuestionKey = null,
  questionLabel = "",
}) => {
  const [busyUuid, setBusyUuid] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  if (!isOpen) return null;

  // Per-question view: only entries whose ops touch this question.
  const visibleEntries = filterQuestionKey
    ? entries.filter((e) =>
        (e.ops || []).some((op) => op.path[0] === filterQuestionKey)
      )
    : entries;

  const toggleExpand = (uuid) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });

  const handleRevert = async (uuid) => {
    setBusyUuid(uuid);
    try {
      await onRevert(uuid);
    } finally {
      setBusyUuid(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          padding: 20,
          borderRadius: 12,
          width: 520,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h4 style={{ margin: 0 }}>
            {filterQuestionKey
              ? `Change History — ${questionLabel || filterQuestionKey}`
              : "Change History"}
          </h4>
          <button onClick={onClose} className="quota-modal-close-button">
            ×
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
          }}
        >
          {visibleEntries.length === 0 ? (
            <div
              style={{
                fontSize: 13,
                color: "#888",
                padding: 24,
                textAlign: "center",
              }}
            >
              {filterQuestionKey
                ? "No changes recorded for this question."
                : "No changes recorded yet."}
            </div>
          ) : (
            visibleEntries.map((entry) => {
              const isRevert = !!entry.revertOf;
              const isOpen = expanded.has(entry.entryUuid);
              const opCount = (entry.ops || []).length;
              return (
                <div
                  key={entry.entryUuid}
                  style={{
                    borderBottom: "1px solid #f1f5f9",
                    opacity: entry.reverted ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "10px 12px",
                    }}
                  >
                    <div
                      title={entry.username || entry.userEmail || "Unknown"}
                      style={{
                        flexShrink: 0,
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: colorOf(entry.username || entry.userEmail),
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {initialOf(entry.username || entry.userEmail)}
                    </div>

                    <div
                      onClick={() => opCount > 0 && toggleExpand(entry.entryUuid)}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        cursor: opCount > 0 ? "pointer" : "default",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: "#1f2937",
                          textDecoration: entry.reverted
                            ? "line-through"
                            : "none",
                          wordBreak: "break-word",
                        }}
                      >
                        {opCount > 0 && (
                          <span style={{ color: "#94a3b8", marginRight: 4 }}>
                            {isOpen ? "▾" : "▸"}
                          </span>
                        )}
                        {isRevert ? "↩ " : ""}
                        {entry.description}
                      </div>
                      <div
                        style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2 }}
                      >
                        {entry.username || entry.userEmail || "Unknown"}
                        {" · "}
                        <span title={new Date(entry.createdAt).toLocaleString()}>
                          {relativeTime(entry.createdAt)}
                        </span>
                        {opCount > 0 && (
                          <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                            · {isOpen ? "hide" : "show"} details
                          </span>
                        )}
                        {entry.reverted && (
                          <span
                            style={{
                              marginLeft: 8,
                              padding: "1px 6px",
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: "#b91c1c",
                              background: "#fee2e2",
                              borderRadius: 4,
                            }}
                          >
                            REVERTED
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => handleRevert(entry.entryUuid)}
                      disabled={entry.reverted || busyUuid === entry.entryUuid}
                      title={
                        entry.reverted
                          ? "Already reverted"
                          : "Revert this change (keeps later edits)"
                      }
                      style={{
                        flexShrink: 0,
                        fontSize: 12,
                        padding: "4px 10px",
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        background: entry.reverted ? "#f1f5f9" : "#fff",
                        color: entry.reverted ? "#94a3b8" : "#334155",
                        cursor:
                          entry.reverted || busyUuid === entry.entryUuid
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      {busyUuid === entry.entryUuid ? "…" : "Revert"}
                    </button>
                  </div>

                  {isOpen && opCount > 0 && (
                    <div
                      style={{
                        margin: "0 12px 10px 52px",
                        padding: "8px 12px",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        fontSize: 12.5,
                        lineHeight: 1.5,
                        wordBreak: "break-word",
                      }}
                    >
                      {(entry.ops || []).map((op, i) => (
                        <OpDetail key={i} op={op} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
          Reverting a change restores only the fields no later change touched —
          later edits are kept.
        </div>
      </div>
    </div>
  );
};

export default ChangeLogModal;
